// GraphQL operations for the Project detail screen and its Colaboradores tab.
//
// Each frontend feature owns its own operations file (see graphql/projects.ts
// for the Projects list). This file backs the Project detail shell (the
// `getProject` header/members query) and the member-management mutations the
// Colaboradores tab uses: invite, role change, and removal. The Cursos,
// profiles, and generations tabs (tasks 16.4-16.7) define their own documents
// in their own files.
//
// Requirements 3.1, 3.2 (member management, owner-only), and the tab surface
// (Req 4.1, 10.1, 10.3 are served by their own tabs). Design → GraphQL Schema
// (Query.getProject, Mutation.inviteUserToProject/updateMemberRole/removeMember)
// and Frontend (Project detail).

import { gql } from '@apollo/client';
import type { Project, ProjectMember, ProjectRole } from '@courseai/domain';

/**
 * The subset of a Project the detail shell needs: identity, name/description
 * for the header, the owner id, and the full member list for the Colaboradores
 * tab. Tab content that needs courses/profiles/generations queries those
 * itself, so this stays lean.
 */
export interface ProjectDetailData {
  readonly getProject:
    | (Pick<Project, 'projectId' | 'ownerUserId' | 'name' | 'description'> & {
        readonly members: ReadonlyArray<
          Pick<ProjectMember, 'userId' | 'email' | 'role' | 'invitedByUserId' | 'createdAt'>
        >;
      })
    | null;
}

/** Variables for the `getProject` query. */
export interface GetProjectVariables {
  readonly projectId: string;
}

/**
 * Load a project's header and members. Membership is enforced server-side; a
 * non-member receives the same authorization error whether or not the project
 * exists (Req 2.7). Identity is derived from the JWT, so none is sent.
 */
export const GET_PROJECT = gql`
  query GetProject($projectId: ID!) {
    getProject(projectId: $projectId) {
      projectId
      ownerUserId
      name
      description
      members {
        userId
        email
        role
        invitedByUserId
        createdAt
      }
    }
  }
`;

/** The member fields returned by the member-management mutations. */
export type MemberResult = Pick<
  ProjectMember,
  'projectId' | 'userId' | 'email' | 'role' | 'invitedByUserId' | 'createdAt'
>;

/** Variables for `inviteUserToProject`. */
export interface InviteMemberVariables {
  readonly input: {
    readonly projectId: string;
    readonly email: string;
    readonly role: ProjectRole;
  };
}

/** Shape of the `inviteUserToProject` mutation result. */
export interface InviteMemberData {
  readonly inviteUserToProject: MemberResult;
}

/**
 * Invite a user to the project by email with a role. Owner-only, enforced
 * server-side (Req 3.1, 3.2). The invitee is resolved by email on the server;
 * the client never sends a userId identity (Req 1.5, 1.6).
 */
export const INVITE_USER_TO_PROJECT = gql`
  mutation InviteUserToProject($input: InviteMemberInput!) {
    inviteUserToProject(input: $input) {
      projectId
      userId
      email
      role
      invitedByUserId
      createdAt
    }
  }
`;

/** Variables for `updateMemberRole`. */
export interface UpdateMemberRoleVariables {
  readonly input: {
    readonly projectId: string;
    readonly userId: string;
    readonly role: ProjectRole;
  };
}

/** Shape of the `updateMemberRole` mutation result. */
export interface UpdateMemberRoleData {
  readonly updateMemberRole: MemberResult;
}

/**
 * Change an existing member's role. Owner-only (Req 3.2). The `userId` here
 * identifies the member being changed, not the caller; the caller's identity
 * still comes from the JWT.
 */
export const UPDATE_MEMBER_ROLE = gql`
  mutation UpdateMemberRole($input: UpdateMemberRoleInput!) {
    updateMemberRole(input: $input) {
      projectId
      userId
      email
      role
      invitedByUserId
      createdAt
    }
  }
`;

/** Variables for `removeMember`. */
export interface RemoveMemberVariables {
  readonly input: {
    readonly projectId: string;
    readonly userId: string;
  };
}

/** Shape of the `removeMember` mutation result. */
export interface RemoveMemberData {
  readonly removeMember: boolean;
}

/**
 * Remove a member from the project. Owner-only (Req 3.2). The server rejects
 * removing the sole owner (last-owner guard, Req 3.8). Returns true on success.
 */
export const REMOVE_MEMBER = gql`
  mutation RemoveMember($input: RemoveMemberInput!) {
    removeMember(input: $input)
  }
`;
